import { Box, Card, Radio, Text, Title, Tooltip } from '@nimbus-ds/components';
import { PromotionTypes } from '@tiendanube/common/src/domains/marketing/promotions/PromotionTypes';
import { Stack } from '@tiendanube/components';
import {
  NUVEMPAGO_ENABLED,
  PROMOTIONS_PAYMENT_OPTIONS_LIMIT_TAG,
  PROMOTIONS_GRANULAR_COMBINATION_TAG,
} from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { Dates, PaymentOptions } from 'domains/Marketing/components';
import { Scope } from 'domains/Marketing/enums';
import { useTranslationMarketing } from 'domains/Marketing/hook';
import { DiscountCombination } from './DiscountCombination';
import { PromotionFormProps } from '../PromotionForm';

type LimitsCardProps = Pick<
  PromotionFormProps,
  'values' | 'errors' | 'onChange' | 'onChangePaymentOptions'
>;

export function LimitsCard({
  values,
  errors,
  onChange,
  onChangePaymentOptions,
}: Readonly<LimitsCardProps>) {
  const t = useTranslationMarketing('promotions.settingsPage.limits');
  const [hasPaymentOptionsTag, hasNuvemPagoEnabled, hasGranularCombination] =
    useHasTags([
      PROMOTIONS_PAYMENT_OPTIONS_LIMIT_TAG,
      NUVEMPAGO_ENABLED,
      PROMOTIONS_GRANULAR_COMBINATION_TAG,
    ]);

  const hasPaymentOptionsLimitEnabled =
    hasNuvemPagoEnabled &&
    values.promotionType === PromotionTypes.PERCENTAGE_OFF;

  return (
    <Card>
      <Card.Header>
        <Stack spacing="none" justify="space-between" align="center">
          <Stack spacing="tight">
            <Title as="h4">{t('title')}</Title>
          </Stack>
        </Stack>
      </Card.Header>
      <Card.Body>
        <Box display="flex" flexDirection="column" gap="6">
          <Text>{t('info')}</Text>

          {values.promotionType !== PromotionTypes.CROSS_SELLING &&
            !hasGranularCombination && (
              <DiscountCombination values={values} onChange={onChange} />
            )}

          <Box display="flex" flexDirection="column" gap="2">
            <Dates values={values} errors={errors} onChange={onChange} />
          </Box>

          {hasPaymentOptionsLimitEnabled && (
            <Box display="flex" flexDirection="column" gap="2">
              <Text fontWeight="bold">{t('paymentOptions.title')}</Text>

              {hasPaymentOptionsTag ? (
                <Box display="flex" flexDirection="column" gap="4">
                  <PaymentOptions
                    values={values}
                    errors={errors}
                    onChange={onChange}
                    onChangePaymentOptions={onChangePaymentOptions}
                    shouldLimitToNuvemPago={
                      values.scopeType === Scope.PRODUCTS ||
                      values.scopeType === Scope.CATEGORIES
                    }
                  />
                </Box>
              ) : (
                <Stack spacing="tight">
                  <Radio
                    name="paymentOptionsScopeType"
                    as="button"
                    label={t('paymentOptions.scope.unlimited')}
                    checked
                  />
                  <Tooltip
                    content={t('paymentOptions.scope.tooltip')}
                    arrow
                    position="bottom"
                  >
                    <Radio
                      name="paymentOptionsScopeType"
                      as="button"
                      label={t('paymentOptions.scope.limited')}
                      disabled
                    />
                  </Tooltip>
                </Stack>
              )}
            </Box>
          )}
        </Box>
      </Card.Body>
    </Card>
  );
}

export default LimitsCard;
